import axios, { isAxiosError } from 'axios';

type UploadBlobOptions = {
  url: string;
  blob: Blob;
  contentType?: string;
  onUploadProgress?: (percentage: number) => void;
  method?: 'post' | 'put' | 'patch';
};

type UploadBlobResult<T> = {
  data: T;
  status: number;
};

type UploadBlobError = {
  status: number | undefined;
  message: string;
};

const uploadBlob = async <T = unknown>({
  url,
  blob,
  contentType = blob.type,
  onUploadProgress,
  method = 'post',
}: UploadBlobOptions): Promise<UploadBlobResult<T>> => {
  try {
    const { data, status } = await axios[method](url, blob, {
      headers: {
        'Content-Type': contentType,
      },
      onUploadProgress: onUploadProgress
        ? ({ loaded, total }) => {
            const percentage = total ? Math.round((loaded / total) * 100) : 0;
            onUploadProgress(percentage);
          }
        : undefined,
    });

    return { data, status };
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        status: error.response?.status,
        message: error.response?.data?.message ?? error.message,
      } satisfies UploadBlobError;
    }

    throw {
      status: undefined,
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
    } satisfies UploadBlobError;
  }
};

export { uploadBlob, type UploadBlobError };